/* eslint-disable indent */
import { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CardMedia,
  CircularProgress,
  Container,
  DialogContent,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import MultiSelect from 'components/QuestionBuilders/MultiSelect';
import SingleSelect from 'components/QuestionBuilders/SingleSelect';
import UserInput from 'components/QuestionBuilders/UserInput';
import MultiSelectPrimary from 'components/QuestionBuilders/MultiSelectPrimary';
import SingleSelectPrimary from 'components/QuestionBuilders/SingleSelectPrimary';
import DomainInfo from 'components/QuestionBuilders/DomainInfo';
import { useAppDispatch } from 'store/hooks';
import { ROUTE } from 'route/RouteEnums';
import { AuthContext } from 'utils/AuthContext';
import { StyledDialog } from 'theme/index';
import DynamicTableRenderer from './DynamicTableRenderer';
import { saveMFSHAnswers } from 'store/slices/mfshSlice';
import FreeTextRenderer from 'components/QuestionBuilders/FreeTextRenderer';

const MFSHAssessment = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [question, setQuestion] = useState<any>({});
  const [type, setType] = useState<string>('');
  const [answers, setAnswers] = useState<any>({});
  const [displayModal, setDisplayModal] = useState<boolean>(false);
  const [displayThankyouModal, setDisplayThankyouModal] = useState<boolean>(false);
  const [displayQuestion, setDisplayQuestion] = useState<boolean>(false);
  const patientId = user?.patientId;

  useEffect(() => {
    if (patientId) {
      submitAnswer({});
    }
  }, [patientId]);

  const submitAnswer = (answerDetails: any) => {
    const data = {
      ...answerDetails,
      patientId: user?.patientId,
    };
    setDisplayQuestion(false);
    dispatch(saveMFSHAnswers(data))
      .unwrap()
      .then((response: any) => {
        if (response.status === 200) {
          const { question } = response.data;
          if (response?.data?.testCompleted) {
            setDisplayThankyouModal(true);
          }
          const questionDetails = {
            questionId: response?.data?.quizId,
            question: question?.text,
            type: question?.type,
            info: question?.info,
            informationSection: question?.informationSection,
            header: question?.header,
            launchType: null,
            heading: question?.heading,
            instruction: question?.instruction,
            progress: response?.data?.progress,
            event: response?.data?.event,
            imageId: question?.imageId,
            prompt: question?.prompt,
            placementOfPrompt: question?.placementOfPrompt,
            subTitle: question?.subTitle,
            warn: question?.warn,
            wellnessSubtitle: question?.wellnessSubtitle,
            assessmentType: 'mfsh',
            buttonText: question?.buttonText,
            tableColumns: question?.tableColumns,
            answerMode: question?.answerMode,
            maxRows: question?.maxRows,
            mapping: question?.mapping,
            testName: 'MFSH',
          };
          setQuestion(questionDetails);
          setAnswers(question?.answerSection);
          setType(question?.answerMode);
          if (question?.type === 'info') {
            setDisplayModal(true);
          }
          setDisplayQuestion(true);
        }
      });
  };

  const handleClose = () => {
    setDisplayModal(false);
  };

  const openModal = () => {
    setDisplayModal(true);
  };

  const handleDomainModal = () => {
    submitAnswer({ quizId: question.questionId });
  };

  const displayQuestions = () => {
    if (!displayThankyouModal) {
      switch (type) {
        case 'radio':
          return <SingleSelect question={question} answers={answers} handleNext={submitAnswer} openModal={openModal} />;
        case 'checkbox':
          return <MultiSelect question={question} answers={answers} handleNext={submitAnswer} openModal={openModal} />;
        case 'inputbox':
          return <UserInput question={question} handleNext={submitAnswer} openModal={openModal} />;
        case 'radioPrimary':
          return (
            <SingleSelectPrimary
              question={question}
              answers={answers}
              handleNext={submitAnswer}
              openModal={openModal}
            />
          );
        case 'checkboxPrimary':
          return (
            <MultiSelectPrimary question={question} answers={answers} handleNext={submitAnswer} openModal={openModal} />
          );
        case 'fixed_table':
        case 'vertical_fixed_table':
          return <DynamicTableRenderer question={question} handleNext={submitAnswer} openModal={openModal} />;
        case 'none':
          return <DomainInfo info={question} imageId={question?.imageId} handleDomainModal={handleDomainModal} />;
        case 'free_text':
          return <FreeTextRenderer question={question} handleNext={submitAnswer} openModal={openModal} />;
      }
    }
  };

  return (
    <Container component='section' maxWidth='md'>
      <Box mt={8}>
        {!displayQuestion && !displayThankyouModal ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          displayQuestions()
        )}{' '}
      </Box>
      {/* Continue later dialog */}
      <StyledDialog open={displayModal} fullWidth={false}>
        <DialogContent>
          <IconButton
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
            }}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
          {question?.type === 'info' ? null : (
            <Typography variant='h1' gutterBottom textAlign={'center'}>
              Need a Break?
            </Typography>
          )}
          <Box margin={'5px 10px'}>
            {question?.type === 'info' ? (
              <Typography>Domain information here</Typography>
            ) : (
              <Typography textAlign={'center'}>
                You can pause and resume the assessment at any time. Be sure to complete this assessment and the M-Wise
                Assessment (next step) at least 4 days before your appointment. Otherwise, your appointment will be
                automatically canceled.
              </Typography>
            )}
          </Box>
          <Grid container item xs={12} mt={4} sx={{ justifyContent: 'center' }}>
            {question?.type !== 'info' ? (
              <Box sx={{ textAlign: { xs: 'center' } }}>
                <Button
                  variant='outlined'
                  sx={{ marginRight: '8px', marginBottom: { xs: 2, sm: 0 } }}
                  onClick={handleClose}
                >
                  No, I will continue
                </Button>
                <Button onClick={() => navigate(ROUTE.DASHBOARD)}>Save and Close</Button>
              </Box>
            ) : (
              <Button onClick={handleClose}> I Understand </Button>
            )}
          </Grid>
        </DialogContent>
      </StyledDialog>

      {/*  Thank you dialog */}
      {displayThankyouModal ? (
        <>
          <Grid container sx={{ display: 'flex', justifyContent: 'center', mb: '24px' }}>
            <CardMedia
              component={'img'}
              src='../images/assessmentCompleted.svg'
              alt='card-img1'
              height='120px'
              sx={{ objectFit: 'contain', width: 'auto' }}
            />
          </Grid>
          <Typography
            gutterBottom
            textAlign={'center'}
            sx={{ fontSize: '36px', fontWeight: 600, fontFamily: 'Playfair Display' }}
          >
            Great Job!
          </Typography>
          <Container
            component='section'
            maxWidth='xl'
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
          >
            <Box display={'flex'} flexDirection={'column'} alignItems={'center'} pb={2} maxWidth={'md'}>
              <Box
                sx={{
                  display: 'flex',

                  flexDirection: 'column',
                  padding: '30px 24px 24px 24px',
                  alignItems: 'flex-start',
                  borderRadius: '8px',
                  background: '#fff',
                  boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.12)',
                }}
              >
                <Box sx={{ fontSize: '18px', lineHeight: '28px', marginTop: '10px' }}>
                  <Typography textAlign={'center'} sx={{ fontSize: '18px', color: '#2C3446' }}>
                    You&apos;ve completed your child&apos;s Medical, Family & Social History. Thank you for sharing this
                    information.
                  </Typography>
                </Box>
                <Box sx={{ fontSize: '18px', lineHeight: '28px', marginTop: '10px' }}>
                  <Typography textAlign={'center'} sx={{ fontSize: '18px', color: '#2C3446' }}>
                    Your final step: Complete the M-Wise Assessment. You can start now or return to your dashboard and
                    finish it later.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Container>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent='center' alignItems='center' mt={4}>
            <Button
              variant='contained'
              onClick={() => navigate(ROUTE.MCAT_TEST)}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Start M-WISE Assessment
            </Button>

            <Button
              variant='outlined'
              onClick={() => navigate(ROUTE.DASHBOARD)}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Return to Dashboard
            </Button>
          </Stack>
        </>
      ) : null}
    </Container>
  );
};

export default MFSHAssessment;
